/**
 * Слой 1 модели прав: закрытый каталог ключей.
 *
 * Домен — корпоративная юридическая служба: локальные нормативные акты и приказы, договоры,
 * входящая и исходящая корреспонденция, шаблоны документов, справочники, база знаний, отчётность,
 * администрирование. Плюс права уровня арендатора (`organization:*`) и слой IAM.
 *
 * Правила, действующие для каждого ключа:
 *
 * 1. Право существует, только если его проверяет какой-нибудь use-case. Ключ, который никто не
 *    читает, — обещание, а не проверка.
 * 2. Ключи не переиспользуются: снятый ключ не возвращается с другим смыслом, иначе исторические
 *    строки `RolePermission` и записи `AuditLog` начинают означать что-то ещё.
 * 3. `requiredLevel` живёт только в коде — это свойство логики, а не данных, и меняется вместе с
 *    use-case.
 * 4. Ключ вида `<resource>:<action>`; `resource` и `action` в мета обязаны совпадать с самим
 *    ключом (проверяется тестом).
 *
 * Глаголы `override`, `unlock`, `reopen` называют обход обычного порядка и **всегда**
 * `dangerous: true`.
 */
export const PERMISSIONS = [
    // ─── Арендатор (организация-клиент) и его подразделения ───
    'organization:read',
    'organization:update',
    'organization:manage_branding',
    'organization:manage_locale',
    'organization:manage_security_policy',
    'organization:manage_signing_key',
    'organization:manage_storage',
    'organization:view_usage',
    'organization:export_data',
    'organization:transfer_ownership',
    'organization:delete',
    'team:read',
    'team:create',
    'team:update',
    'team:delete',
    'team:manage_members',
    // ─── Люди, роли и права ───
    'user:read',
    'user:invite',
    'user:update',
    'user:suspend',
    'user:reactivate',
    'user:delete',
    'user:reset_password',
    'user:reset_mfa',
    'user:force_logout',
    'user:read_sessions',
    'user:impersonate',
    'invitation:read',
    'invitation:create',
    'invitation:resend',
    'invitation:revoke',
    'employee:read',
    'employee:update',
    'employee:view_org_chart',
    'employee:view_capacity',
    'employee:manage_employment',
    'employee:view_personal_data',
    'employee:view_cost_rate',
    'employee:manage_cost_rate',
    'employee:view_drilldown',
    'role:read',
    'role:create',
    'role:update',
    'role:delete',
    'role:assign',
    'role:revoke',
    'permission:read',
    'permission:override_read',
    'permission:override',
    'permission:explain',
    'acl:read',
    'acl:grant',
    'acl:revoke',
    // ─── Локальные нормативные акты, приказы и ознакомление ───
    'act:read',
    'act:create',
    'act:update',
    'act:delete',
    'act:approve',
    'act:publish',
    'act:repeal',
    'act:archive',
    'act:export',
    'act:view_history',
    'act:restore_version',
    'act:manage_relations',
    'act:consolidate',
    'act:manage_review_reminder',
    'act:manage_acl',
    'acknowledgement:read',
    'acknowledgement:request',
    'acknowledgement:confirm',
    'acknowledgement:view_report',
    'acknowledgement:manage',
    'order:read',
    'order:create',
    'order:update',
    'order:delete',
    'order:register',
    'order:approve',
    'order:sign',
    'order:revoke',
    'order:export',
    'order:view_history',
    'order:manage_acl',
    // ─── Договоры, плейбуки и согласование ───
    'contract:read',
    'contract:create',
    'contract:update',
    'contract:delete',
    'contract:approve',
    'contract:sign',
    'contract:change_status',
    'contract:terminate',
    'contract:export',
    'contract:view_history',
    'contract:manage_acl',
    'contract:view_financial_terms',
    'contract:manage_financial_terms',
    'contract:view_confidential',
    'contract:analyze',
    'playbook:read',
    'playbook:create',
    'playbook:update',
    'playbook:delete',
    'playbook:publish',
    'playbook:export',
    'approval:read',
    'approval:create',
    'approval:update',
    'approval:delete',
    'approval:decide',
    'approval:delegate',
    'approval:override',
    // ─── Входящая и исходящая корреспонденция ───
    'letter:read',
    'letter:register',
    'letter:create',
    'letter:update',
    'letter:delete',
    'letter:assign',
    'letter:resolve',
    'letter:manage_deadline',
    'letter:approve',
    'letter:sign',
    'letter:dispatch',
    'letter:export',
    'letter:view_history',
    'letter:read_journal',
    'letter:manage_numbering',
    'letter:manage_acl',
    // ─── Шаблоны документов ───
    'template:read',
    'template:create',
    'template:update',
    'template:delete',
    'template:approve',
    'template:publish',
    'template:archive',
    'template:export',
    'template:view_history',
    'template:restore_version',
    'template:manage_acl',
    'template:generate_document',
    /**
     * Выгрузить собранный проект документа файлом — подписанной ссылкой на чтение.
     *
     * **Отдельно от `template:generate_document`, и это требование правила, а не вкус**
     * (`rules/import-export.mdc`, п. 12): «возможность выгрузить одним файлом не следует
     * автоматически из права собрать документ на экране». Проект, собранный ассистентом, лежит в
     * контуре и под журналом; файл, ушедший в почту заказчика, из контура вышел, и дальше система за
     * него не отвечает — тот же разрез, что у `file:download` против `file:read` и у
     * `document:download` против `document:read`.
     *
     * **Ресурс `template`, а не `document`, и это несущее различие.** Проект — не документ в
     * обращении: его нет в реестре, на него нельзя сослаться, ответ ассистента его не видит
     * (решение **без номера ADR**, и это сверено 05.09.2026, а не предположено: 0019 занят
     * написанным ADR про поиск по реестру, 0021 — решением об outbox, которое с этой же датой
     * написано файлом. Резервировать номер комментарием в коде не работает — следующая волна берёт
     * свободный номер по каталогу файлов; сведение списка за STORY-043-12,
     * `docs/product/08-план-спринтов.md`, «Что выяснилось при написании историй»). Ключ
     * под `document:` слил бы в матрице прав два материала с разным юридическим весом — ровно то
     * различие, ради которого заведён `document:withdraw`.
     *
     * `dangerous: true` — единственное последствие действия в том, что копия покидает контур, и
     * копия эта помечена «требует проверки»: выпущенный без проверки проект выглядит как документ
     * организации. Тот же довод, что у `document:set_external_processing`, и намеренно **не** тот,
     * что у `document:download`: скачивание принятого документа — обычная работа юриста, а выгрузка
     * машинного проекта наружу — решение.
     *
     * `requiredLevel: null` — по той же причине, что у `document:download`: ресурсного ACL в проекте
     * ещё нет (EPIC-015), и ключ с уровнем отказывал бы всем, включая владельца.
     */
    'template:export_document',
    // ─── Справочники и контрагенты ───
    'reference:read',
    'reference:create',
    'reference:update',
    'reference:delete',
    'reference:export',
    'reference:import',
    'reference_entry:read',
    'reference_entry:create',
    'reference_entry:update',
    'reference_entry:delete',
    'counterparty:read',
    'counterparty:create',
    'counterparty:update',
    'counterparty:delete',
    'counterparty:export',
    'counterparty:view_details',
    'counterparty:manage_contacts',
    // ─── База знаний ───
    'kb_space:read',
    'kb_space:create',
    'kb_space:update',
    'kb_space:delete',
    'kb_space:manage_acl',
    'kb_space:export',
    'kb_space:import',
    'kb_article:read',
    'kb_article:create',
    'kb_article:update',
    'kb_article:delete',
    'kb_article:approve',
    'kb_article:publish',
    'kb_article:export',
    'kb_article:view_history',
    'kb_article:restore_version',
    'kb_article:manage_tags',
    'kb_article:comment',
    // ─── Файлы и вложения ───
    /**
     * Реестр принятых документов — вход материала в контур.
     *
     * Отдельно от `file:*`, и это не дубль. `file:read` требует ресурсного уровня (`VIEWER`), то есть
     * грант **на конкретный файл**; ресурсного ACL в проекте ещё нет (EPIC-015), и ключ, для которого
     * нельзя выдать уровень, отказывает всем — включая владельца. Отдельно от `act:*`: акт это
     * карточка нормативного документа со статусами и связями (EPIC-026), а здесь принятый файл,
     * разобранный на пункты, на которые ссылается ответ ассистента.
     *
     * `requiredLevel: null` — решение того же порядка: пока документ виден всем, кто вправе читать
     * документы организации, разграничения внутри организации нет. Оно появится вместе с ACL, и тогда
     * уровень у этих ключей станет не `null` — осознанным изменением, видимым в снапшоте матрицы.
     */
    'document:read',
    'document:upload',
    /**
     * Пометить документ допустимым (или недопустимым) к обработке за периметром — поштучно.
     *
     * Отдельный ключ, а не доля `document:upload`, и это ровно тот инвариант, ради которого гейт
     * периметра существует: принести файл в контур и разрешить ему выйти наружу — разные решения.
     * Слив их в один ключ вернул бы прежний дефект на шаг назад: тот, кто задаёт вопрос по документу,
     * сам же и открывал бы ему дорогу к внешней модели.
     *
     * `dangerous: true`: единственное последствие этого действия — материал заказчика получает право
     * покинуть периметр. `requiredLevel: null` — по той же причине, что у двух ключей выше:
     * ресурсного ACL в проекте ещё нет, а ключ с уровнем отказывал бы всем, включая владельца.
     *
     * **Состав ролей узкий намеренно.** Ключ выдан `owner`, `head_of_legal` и `compliance` — тем, кто
     * отвечает за периметр на аттестации. `lawyer`, приносящий документы, его сегодня не держит:
     * риск, названный в EPIC-005 прямым текстом, — «признак выставят массово „разрешено“, чтобы не
     * мешал», и ближайший способ его получить это выдать пометку тому же человеку, которому она
     * мешает. Расширение состава — продуктовое решение, и оно видно в снапшоте матрицы как расширение
     * прав, а не как строка JSON.
     */
    'document:set_external_processing',
    /**
     * Правка карточки принятого документа: наименование и период действия его редакции.
     *
     * Отдельно от `document:upload`, как `file:update` отделён от `file:upload` и `act:update` от
     * `act:create`: принести документ в контур и переписать то, чем он назван в реестре, — разные
     * решения, и второе доступно позже первого, когда исходный файл уже никто не открывает. Ключ
     * покрывает **карточку**, а не содержимое: текст вступившей в силу редакции неизменяем при любом
     * праве (`rules/legal-documents.mdc`, п. 2), и правка порождает новую редакцию, а не `UPDATE`.
     */
    'document:update',
    /**
     * Вывод документа из обращения и возврат его в обращение.
     *
     * **Это не удаление, и ключ назван так, чтобы это было видно в матрице прав.** Строка документа
     * не исчезает и исчезнуть не может: на неё ссылаются журнал доступа, ответы ассистента и история
     * на дату (`CLAUDE.md`, «Аудит и сроки хранения»; `documents` числится в `no_delete` файла
     * `prisma/sql/01-grants.sql`, то есть привилегии `DELETE` у приложения нет вовсе). Выведенный
     * документ уходит из реестра и из материала ассистента и остаётся открываемым по прямой ссылке.
     *
     * Отдельно от `document:update`, хотя обе операции меняют строку документа: опечатка в
     * наименовании и решение «этот документ больше не в работе» — разного веса, и вторым ассистент
     * перестаёт видеть основание, на которое вчера ссылался. Один ключ на обе стороны, как у
     * признака периметра: право вернуть документ не должно быть уже права его вывести, иначе
     * выведенный по ошибке возвращать было бы некому.
     */
    'document:withdraw',
    /**
     * Скачивание исходного файла принятого документа подписанной ссылкой на чтение.
     *
     * Отдельно от `document:read`, и по той же причине, по которой `file:download` в этом каталоге
     * отделён от `file:read`: чтение в браузере оставляет данные внутри периметра и под журналом, а
     * скачивание выносит копию наружу, где система за неё больше не отвечает. Ровно эту разницу
     * фиксирует и словарь журнала — `legal_document.downloaded` заведено отдельно от
     * `legal_document.viewed`, — и право, слитое с чтением, сделало бы это различие
     * незапротоколированным на входе.
     *
     * `requiredLevel: null` — по той же причине, что у `document:read`: ресурсного ACL в проекте ещё
     * нет (EPIC-015), и ключ с уровнем отказывал бы всем, включая владельца.
     */
    'document:download',
    /**
     * Указать — или снять — официальные реквизиты акта у принятого документа (EPIC-050,
     * STORY-050-07): вид, дата, номер — то, что опознаёт документ как конкретный государственный акт
     * для последующей сверки редакции.
     *
     * Отдельно от `document:update`, хотя обе операции правят карточку. Опечатка в наименовании и
     * решение «этот документ — вот этот конкретно акт» — разного веса: второе открывает документу
     * дорогу к сверке с внешним порталом, и ошибка опознания даёт пометку «актуально» на чужом акте
     * (ADR-0015). Сваленное в `document:update`, решение осталось бы неотличимым в журнале от правки
     * заголовка.
     */
    'document:set_official_act_reference',
    /**
     * Запустить сверку редакции документа с `publication.pravo.gov.ru` (EPIC-050, STORY-050-07,
     * ADR-0015, вариант Б).
     *
     * **Отдельный ключ, а не доля `document:read`.** Чтение карточки — в том числе состояния
     * применимости сверки — не пересекает периметр; это действие пересекает, вынося наружу реквизиты
     * акта. Тот же разрез, что у `document:set_external_processing`: право читать документ не должно
     * автоматически открывать дорогу его реквизитам наружу.
     *
     * `dangerous: true` — той же природы, что у `document:set_external_processing`: реквизиты акта
     * сами по себе публичны, но их **перечень** раскрывает, чем занимается организация (ADR-0015,
     * «Что уходит наружу»). Действие не читает и не изменяет содержимое документа и потому не
     * запрашивает `document:read`/`document:update` — оно самодостаточно как решение «этому документу
     * разрешено выйти на сверку сейчас».
     */
    'document:check_edition',
    'file:read',
    'file:download',
    'file:upload',
    'file:update',
    'file:delete',
    'file:restore',
    'file:manage_versions',
    'file:manage_folders',
    'file:manage_acl',
    'file:view_quarantined',
    'file:download_quarantined',
    'file:manage_retention',
    'file:view_quota',
    // ─── Отчётность ───
    'report:read',
    'report:read_team',
    'report:read_org',
    'report:create',
    'report:update',
    'report:delete',
    'report:export',
    'report:schedule',
    'dashboard:read',
    'dashboard:create',
    'dashboard:update',
    'dashboard:delete',
    'dashboard:share',
    // ─── ИИ-ассистент ───
    'ai:use',
    'ai:use_document_context',
    'ai:read_answer_trace',
    'ai:read_own_threads',
    'ai:read_any_thread',
    'ai:delete_thread',
    'ai:run_tools',
    'ai:view_usage',
    'ai:manage_budget',
    'ai:manage_tool_policy',
    'ai:configure_providers',
    'ai:reindex_embeddings',
    // ─── Платформа и администрирование ───
    'audit:read',
    'audit:read_security',
    'audit:read_access',
    'audit:export',
    'audit:manage_retention',
    'settings:read',
    'settings:update',
    'settings:manage_email',
    'settings:manage_storage_backend',
    'settings:manage_backup',
    'settings:run_backup',
    'settings:restore_backup',
    'settings:view_system_health',
    'settings:manage_feature_flags',
    'retention:read',
    'retention:manage',
    'retention:unlock',
    'api_token:read_own',
    'api_token:create_own',
    'api_token:revoke_own',
    'api_token:read_any',
    'api_token:revoke_any',
    'notification:read_own',
    'notification:manage_preferences_own',
    'notification:manage_templates',
    'job:read',
    'job:retry',
    'search:reindex',
    /* ── провайдеры моделей и след их работы (EPIC-005) ── */
    'ai_provider:read',
    'ai_provider:write',
    'external_call_log:read',
    'evaluation_run:read',
];
/** Ограниченные контексты, по которым сгруппированы ключи (docs/architecture/overview.md). */
export const PERMISSION_DOMAINS = [
    'organization',
    'iam',
    'act',
    'contract',
    'correspondence',
    'template',
    'reference',
    'knowledge',
    'file',
    'analytics',
    'ai',
    'platform',
];
export const PERMISSION_META = {
    'organization:read': {
        resource: 'organization',
        action: 'read',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.organization.read',
    },
    'organization:update': {
        resource: 'organization',
        action: 'update',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.organization.update',
    },
    'organization:manage_branding': {
        resource: 'organization',
        action: 'manage_branding',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.organization.manage_branding',
    },
    'organization:manage_locale': {
        resource: 'organization',
        action: 'manage_locale',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.organization.manage_locale',
    },
    'organization:manage_security_policy': {
        resource: 'organization',
        action: 'manage_security_policy',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.manage_security_policy',
    },
    'organization:manage_signing_key': {
        resource: 'organization',
        action: 'manage_signing_key',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.manage_signing_key',
    },
    'organization:manage_storage': {
        resource: 'organization',
        action: 'manage_storage',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.manage_storage',
    },
    'organization:view_usage': {
        resource: 'organization',
        action: 'view_usage',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.organization.view_usage',
    },
    'organization:export_data': {
        resource: 'organization',
        action: 'export_data',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.export_data',
    },
    'organization:transfer_ownership': {
        resource: 'organization',
        action: 'transfer_ownership',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.transfer_ownership',
    },
    'organization:delete': {
        resource: 'organization',
        action: 'delete',
        domain: 'organization',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.organization.delete',
    },
    'team:read': {
        resource: 'team',
        action: 'read',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.team.read',
    },
    'team:create': {
        resource: 'team',
        action: 'create',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.team.create',
    },
    'team:update': {
        resource: 'team',
        action: 'update',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.team.update',
    },
    'team:delete': {
        resource: 'team',
        action: 'delete',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.team.delete',
    },
    'team:manage_members': {
        resource: 'team',
        action: 'manage_members',
        domain: 'organization',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.team.manage_members',
    },
    'user:read': {
        resource: 'user',
        action: 'read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.read',
    },
    'user:invite': {
        resource: 'user',
        action: 'invite',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.invite',
    },
    'user:update': {
        resource: 'user',
        action: 'update',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.update',
    },
    'user:suspend': {
        resource: 'user',
        action: 'suspend',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.user.suspend',
    },
    'user:reactivate': {
        resource: 'user',
        action: 'reactivate',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.reactivate',
    },
    'user:delete': {
        resource: 'user',
        action: 'delete',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.user.delete',
    },
    'user:reset_password': {
        resource: 'user',
        action: 'reset_password',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.user.reset_password',
    },
    'user:reset_mfa': {
        resource: 'user',
        action: 'reset_mfa',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.user.reset_mfa',
    },
    'user:force_logout': {
        resource: 'user',
        action: 'force_logout',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.force_logout',
    },
    'user:read_sessions': {
        resource: 'user',
        action: 'read_sessions',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.user.read_sessions',
    },
    'user:impersonate': {
        resource: 'user',
        action: 'impersonate',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.user.impersonate',
    },
    'invitation:read': {
        resource: 'invitation',
        action: 'read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.invitation.read',
    },
    'invitation:create': {
        resource: 'invitation',
        action: 'create',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.invitation.create',
    },
    'invitation:resend': {
        resource: 'invitation',
        action: 'resend',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.invitation.resend',
    },
    'invitation:revoke': {
        resource: 'invitation',
        action: 'revoke',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.invitation.revoke',
    },
    'employee:read': {
        resource: 'employee',
        action: 'read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.employee.read',
    },
    'employee:update': {
        resource: 'employee',
        action: 'update',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.employee.update',
    },
    'employee:view_org_chart': {
        resource: 'employee',
        action: 'view_org_chart',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.employee.view_org_chart',
    },
    'employee:view_capacity': {
        resource: 'employee',
        action: 'view_capacity',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.employee.view_capacity',
    },
    'employee:manage_employment': {
        resource: 'employee',
        action: 'manage_employment',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.employee.manage_employment',
    },
    'employee:view_personal_data': {
        resource: 'employee',
        action: 'view_personal_data',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.employee.view_personal_data',
    },
    'employee:view_cost_rate': {
        resource: 'employee',
        action: 'view_cost_rate',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.employee.view_cost_rate',
    },
    'employee:manage_cost_rate': {
        resource: 'employee',
        action: 'manage_cost_rate',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.employee.manage_cost_rate',
    },
    'employee:view_drilldown': {
        resource: 'employee',
        action: 'view_drilldown',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.employee.view_drilldown',
    },
    'role:read': {
        resource: 'role',
        action: 'read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.role.read',
    },
    'role:create': {
        resource: 'role',
        action: 'create',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.role.create',
    },
    'role:update': {
        resource: 'role',
        action: 'update',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.role.update',
    },
    'role:delete': {
        resource: 'role',
        action: 'delete',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.role.delete',
    },
    'role:assign': {
        resource: 'role',
        action: 'assign',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.role.assign',
    },
    'role:revoke': {
        resource: 'role',
        action: 'revoke',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.role.revoke',
    },
    'permission:read': {
        resource: 'permission',
        action: 'read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.permission.read',
    },
    'permission:override_read': {
        resource: 'permission',
        action: 'override_read',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.permission.override_read',
    },
    'permission:override': {
        resource: 'permission',
        action: 'override',
        domain: 'iam',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.permission.override',
    },
    'permission:explain': {
        resource: 'permission',
        action: 'explain',
        domain: 'iam',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.permission.explain',
    },
    'acl:read': {
        resource: 'acl',
        action: 'read',
        domain: 'iam',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.acl.read',
    },
    'acl:grant': {
        resource: 'acl',
        action: 'grant',
        domain: 'iam',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.acl.grant',
    },
    'acl:revoke': {
        resource: 'acl',
        action: 'revoke',
        domain: 'iam',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.acl.revoke',
    },
    /*
     * `requiredLevel: null` у ключей акта, которые уже проверяет какой-нибудь use-case (EPIC-026).
     *
     * Уровень — свойство логики, а не данных, и меняется вместе с use-case (правило 3 в шапке этого
     * файла). Пока ресурсного ACL в проекте нет (EPIC-015), `authorizeResource` по ключу с уровнем
     * отвечает «нет» **всем**, включая владельца организации: ровно на этом уже спотыкались `file:*`, и
     * экран документов оказался недоступен никому (`domain/document/access/document-access.policy.ts`).
     * Объявленный заранее уровень — это не строгость, а неработающий маршрут.
     *
     * Ключи без своего use-case (`act:approve`, `act:archive`, `act:delete`, …) уровень сохраняют: они
     * никем не проверяются, и менять у них что-либо значило бы решать за истории, которых ещё нет.
     */
    'act:read': {
        resource: 'act',
        action: 'read',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.act.read',
    },
    'document:read': {
        resource: 'document',
        action: 'read',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.document.read',
    },
    'document:upload': {
        resource: 'document',
        action: 'upload',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.document.upload',
    },
    'document:set_external_processing': {
        resource: 'document',
        action: 'set_external_processing',
        domain: 'act',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.document.set_external_processing',
    },
    'document:update': {
        resource: 'document',
        action: 'update',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.document.update',
    },
    'document:withdraw': {
        resource: 'document',
        action: 'withdraw',
        domain: 'act',
        requiredLevel: null,
        /*
         * `dangerous: true`, хотя ничего не утекает и ничего не стирается.
         *
         * Флаг означает «требует подтверждения в UI и внимания на ревью», а не «опасно для данных».
         * Вывод документа из обращения убирает основание из материала ассистента: с этого момента на
         * вопрос, ответ на который вчера опирался на этот пункт, система отвечает «не знаю». Ошибка не
         * проявляется отказом — она проявляется обеднённым ответом, который выглядит нормальным.
         */
        dangerous: true,
        descriptionKey: 'permission.document.withdraw',
    },
    'document:download': {
        resource: 'document',
        action: 'download',
        domain: 'act',
        requiredLevel: null,
        /*
         * `dangerous: false` — то же значение, что у `file:download`, и это не небрежность.
         *
         * Флаг требует подтверждения в интерфейсе на каждое действие, а скачивание документа — обычная
         * работа юриста, а не редкое решение: пометить её опасной значит приучить нажимать «да» не
         * читая, и обесценить флаг там, где он стоит по делу (`document:set_external_processing`). Своё
         * «громче обычного» у этой операции всё равно есть — `legal_document.downloaded` записан в
         * журнал с уровнем `WARNING`, в отличие от `INFO` у просмотра.
         */
        dangerous: false,
        descriptionKey: 'permission.document.download',
    },
    'document:set_official_act_reference': {
        resource: 'document',
        action: 'set_official_act_reference',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.document.set_official_act_reference',
    },
    'document:check_edition': {
        resource: 'document',
        action: 'check_edition',
        domain: 'act',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.document.check_edition',
    },
    'act:create': {
        resource: 'act',
        action: 'create',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.act.create',
    },
    'act:update': {
        resource: 'act',
        action: 'update',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.act.update',
    },
    'act:delete': {
        resource: 'act',
        action: 'delete',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.act.delete',
    },
    'act:approve': {
        resource: 'act',
        action: 'approve',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.act.approve',
    },
    'act:publish': {
        resource: 'act',
        action: 'publish',
        domain: 'act',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.act.publish',
    },
    'act:repeal': {
        resource: 'act',
        action: 'repeal',
        domain: 'act',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.act.repeal',
    },
    'act:archive': {
        resource: 'act',
        action: 'archive',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.act.archive',
    },
    'act:export': {
        resource: 'act',
        action: 'export',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.act.export',
    },
    'act:view_history': {
        resource: 'act',
        action: 'view_history',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.act.view_history',
    },
    'act:restore_version': {
        resource: 'act',
        action: 'restore_version',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.act.restore_version',
    },
    'act:manage_relations': {
        resource: 'act',
        action: 'manage_relations',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.act.manage_relations',
    },
    'act:consolidate': {
        resource: 'act',
        action: 'consolidate',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.act.consolidate',
    },
    'act:manage_review_reminder': {
        resource: 'act',
        action: 'manage_review_reminder',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.act.manage_review_reminder',
    },
    'act:manage_acl': {
        resource: 'act',
        action: 'manage_acl',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.act.manage_acl',
    },
    'acknowledgement:read': {
        resource: 'acknowledgement',
        action: 'read',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.acknowledgement.read',
    },
    'acknowledgement:request': {
        resource: 'acknowledgement',
        action: 'request',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.acknowledgement.request',
    },
    'acknowledgement:confirm': {
        resource: 'acknowledgement',
        action: 'confirm',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.acknowledgement.confirm',
    },
    'acknowledgement:view_report': {
        resource: 'acknowledgement',
        action: 'view_report',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.acknowledgement.view_report',
    },
    'acknowledgement:manage': {
        resource: 'acknowledgement',
        action: 'manage',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.acknowledgement.manage',
    },
    'order:read': {
        resource: 'order',
        action: 'read',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.order.read',
    },
    'order:create': {
        resource: 'order',
        action: 'create',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.order.create',
    },
    'order:update': {
        resource: 'order',
        action: 'update',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.order.update',
    },
    'order:delete': {
        resource: 'order',
        action: 'delete',
        domain: 'act',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.order.delete',
    },
    'order:register': {
        resource: 'order',
        action: 'register',
        domain: 'act',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.order.register',
    },
    'order:approve': {
        resource: 'order',
        action: 'approve',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.order.approve',
    },
    'order:sign': {
        resource: 'order',
        action: 'sign',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.order.sign',
    },
    'order:revoke': {
        resource: 'order',
        action: 'revoke',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.order.revoke',
    },
    'order:export': {
        resource: 'order',
        action: 'export',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.order.export',
    },
    'order:view_history': {
        resource: 'order',
        action: 'view_history',
        domain: 'act',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.order.view_history',
    },
    'order:manage_acl': {
        resource: 'order',
        action: 'manage_acl',
        domain: 'act',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.order.manage_acl',
    },
    'contract:read': {
        resource: 'contract',
        action: 'read',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.contract.read',
    },
    'contract:create': {
        resource: 'contract',
        action: 'create',
        domain: 'contract',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.contract.create',
    },
    'contract:update': {
        resource: 'contract',
        action: 'update',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.contract.update',
    },
    'contract:delete': {
        resource: 'contract',
        action: 'delete',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.contract.delete',
    },
    'contract:approve': {
        resource: 'contract',
        action: 'approve',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.contract.approve',
    },
    'contract:sign': {
        resource: 'contract',
        action: 'sign',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.contract.sign',
    },
    'contract:change_status': {
        resource: 'contract',
        action: 'change_status',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.contract.change_status',
    },
    'contract:terminate': {
        resource: 'contract',
        action: 'terminate',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.contract.terminate',
    },
    'contract:export': {
        resource: 'contract',
        action: 'export',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.contract.export',
    },
    'contract:view_history': {
        resource: 'contract',
        action: 'view_history',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.contract.view_history',
    },
    'contract:manage_acl': {
        resource: 'contract',
        action: 'manage_acl',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.contract.manage_acl',
    },
    'contract:view_financial_terms': {
        resource: 'contract',
        action: 'view_financial_terms',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: true,
        descriptionKey: 'permission.contract.view_financial_terms',
    },
    'contract:manage_financial_terms': {
        resource: 'contract',
        action: 'manage_financial_terms',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.contract.manage_financial_terms',
    },
    'contract:view_confidential': {
        resource: 'contract',
        action: 'view_confidential',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: true,
        descriptionKey: 'permission.contract.view_confidential',
    },
    'contract:analyze': {
        resource: 'contract',
        action: 'analyze',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.contract.analyze',
    },
    'playbook:read': {
        resource: 'playbook',
        action: 'read',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.playbook.read',
    },
    'playbook:create': {
        resource: 'playbook',
        action: 'create',
        domain: 'contract',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.playbook.create',
    },
    'playbook:update': {
        resource: 'playbook',
        action: 'update',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.playbook.update',
    },
    'playbook:delete': {
        resource: 'playbook',
        action: 'delete',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.playbook.delete',
    },
    'playbook:publish': {
        resource: 'playbook',
        action: 'publish',
        domain: 'contract',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.playbook.publish',
    },
    'playbook:export': {
        resource: 'playbook',
        action: 'export',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.playbook.export',
    },
    'approval:read': {
        resource: 'approval',
        action: 'read',
        domain: 'contract',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.approval.read',
    },
    'approval:create': {
        resource: 'approval',
        action: 'create',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.approval.create',
    },
    'approval:update': {
        resource: 'approval',
        action: 'update',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.approval.update',
    },
    'approval:delete': {
        resource: 'approval',
        action: 'delete',
        domain: 'contract',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.approval.delete',
    },
    'approval:decide': {
        resource: 'approval',
        action: 'decide',
        domain: 'contract',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.approval.decide',
    },
    'approval:delegate': {
        resource: 'approval',
        action: 'delegate',
        domain: 'contract',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.approval.delegate',
    },
    'approval:override': {
        resource: 'approval',
        action: 'override',
        domain: 'contract',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.approval.override',
    },
    'letter:read': {
        resource: 'letter',
        action: 'read',
        domain: 'correspondence',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.letter.read',
    },
    'letter:register': {
        resource: 'letter',
        action: 'register',
        domain: 'correspondence',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.letter.register',
    },
    'letter:create': {
        resource: 'letter',
        action: 'create',
        domain: 'correspondence',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.letter.create',
    },
    'letter:update': {
        resource: 'letter',
        action: 'update',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.letter.update',
    },
    'letter:delete': {
        resource: 'letter',
        action: 'delete',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.letter.delete',
    },
    'letter:assign': {
        resource: 'letter',
        action: 'assign',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.letter.assign',
    },
    'letter:resolve': {
        resource: 'letter',
        action: 'resolve',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.letter.resolve',
    },
    'letter:manage_deadline': {
        resource: 'letter',
        action: 'manage_deadline',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.letter.manage_deadline',
    },
    'letter:approve': {
        resource: 'letter',
        action: 'approve',
        domain: 'correspondence',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.letter.approve',
    },
    'letter:sign': {
        resource: 'letter',
        action: 'sign',
        domain: 'correspondence',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.letter.sign',
    },
    'letter:dispatch': {
        resource: 'letter',
        action: 'dispatch',
        domain: 'correspondence',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.letter.dispatch',
    },
    'letter:export': {
        resource: 'letter',
        action: 'export',
        domain: 'correspondence',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.letter.export',
    },
    'letter:view_history': {
        resource: 'letter',
        action: 'view_history',
        domain: 'correspondence',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.letter.view_history',
    },
    'letter:read_journal': {
        resource: 'letter',
        action: 'read_journal',
        domain: 'correspondence',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.letter.read_journal',
    },
    'letter:manage_numbering': {
        resource: 'letter',
        action: 'manage_numbering',
        domain: 'correspondence',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.letter.manage_numbering',
    },
    'letter:manage_acl': {
        resource: 'letter',
        action: 'manage_acl',
        domain: 'correspondence',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.letter.manage_acl',
    },
    'template:read': {
        resource: 'template',
        action: 'read',
        domain: 'template',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.template.read',
    },
    'template:create': {
        resource: 'template',
        action: 'create',
        domain: 'template',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.template.create',
    },
    'template:update': {
        resource: 'template',
        action: 'update',
        domain: 'template',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.template.update',
    },
    'template:delete': {
        resource: 'template',
        action: 'delete',
        domain: 'template',
        requiredLevel: 'EDITOR',
        dangerous: true,
        descriptionKey: 'permission.template.delete',
    },
    'template:approve': {
        resource: 'template',
        action: 'approve',
        domain: 'template',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.template.approve',
    },
    'template:publish': {
        resource: 'template',
        action: 'publish',
        domain: 'template',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.template.publish',
    },
    'template:archive': {
        resource: 'template',
        action: 'archive',
        domain: 'template',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.template.archive',
    },
    'template:export': {
        resource: 'template',
        action: 'export',
        domain: 'template',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.template.export',
    },
    'template:view_history': {
        resource: 'template',
        action: 'view_history',
        domain: 'template',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.template.view_history',
    },
    'template:restore_version': {
        resource: 'template',
        action: 'restore_version',
        domain: 'template',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.template.restore_version',
    },
    'template:manage_acl': {
        resource: 'template',
        action: 'manage_acl',
        domain: 'template',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.template.manage_acl',
    },
    'template:generate_document': {
        resource: 'template',
        action: 'generate_document',
        domain: 'template',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.template.generate_document',
    },
    'template:export_document': {
        resource: 'template',
        action: 'export_document',
        domain: 'template',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.template.export_document',
    },
    'reference:read': {
        resource: 'reference',
        action: 'read',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference.read',
    },
    'reference:create': {
        resource: 'reference',
        action: 'create',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference.create',
    },
    'reference:update': {
        resource: 'reference',
        action: 'update',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference.update',
    },
    'reference:delete': {
        resource: 'reference',
        action: 'delete',
        domain: 'reference',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.reference.delete',
    },
    'reference:export': {
        resource: 'reference',
        action: 'export',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference.export',
    },
    'reference:import': {
        resource: 'reference',
        action: 'import',
        domain: 'reference',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.reference.import',
    },
    'reference_entry:read': {
        resource: 'reference_entry',
        action: 'read',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference_entry.read',
    },
    'reference_entry:create': {
        resource: 'reference_entry',
        action: 'create',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference_entry.create',
    },
    'reference_entry:update': {
        resource: 'reference_entry',
        action: 'update',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference_entry.update',
    },
    'reference_entry:delete': {
        resource: 'reference_entry',
        action: 'delete',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.reference_entry.delete',
    },
    'counterparty:read': {
        resource: 'counterparty',
        action: 'read',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.counterparty.read',
    },
    'counterparty:create': {
        resource: 'counterparty',
        action: 'create',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.counterparty.create',
    },
    'counterparty:update': {
        resource: 'counterparty',
        action: 'update',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.counterparty.update',
    },
    'counterparty:delete': {
        resource: 'counterparty',
        action: 'delete',
        domain: 'reference',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.counterparty.delete',
    },
    'counterparty:export': {
        resource: 'counterparty',
        action: 'export',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.counterparty.export',
    },
    'counterparty:view_details': {
        resource: 'counterparty',
        action: 'view_details',
        domain: 'reference',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.counterparty.view_details',
    },
    'counterparty:manage_contacts': {
        resource: 'counterparty',
        action: 'manage_contacts',
        domain: 'reference',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.counterparty.manage_contacts',
    },
    'kb_space:read': {
        resource: 'kb_space',
        action: 'read',
        domain: 'knowledge',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.kb_space.read',
    },
    'kb_space:create': {
        resource: 'kb_space',
        action: 'create',
        domain: 'knowledge',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.kb_space.create',
    },
    'kb_space:update': {
        resource: 'kb_space',
        action: 'update',
        domain: 'knowledge',
        requiredLevel: 'MANAGER',
        dangerous: false,
        descriptionKey: 'permission.kb_space.update',
    },
    'kb_space:delete': {
        resource: 'kb_space',
        action: 'delete',
        domain: 'knowledge',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.kb_space.delete',
    },
    'kb_space:manage_acl': {
        resource: 'kb_space',
        action: 'manage_acl',
        domain: 'knowledge',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.kb_space.manage_acl',
    },
    'kb_space:export': {
        resource: 'kb_space',
        action: 'export',
        domain: 'knowledge',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.kb_space.export',
    },
    'kb_space:import': {
        resource: 'kb_space',
        action: 'import',
        domain: 'knowledge',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.kb_space.import',
    },
    'kb_article:read': {
        resource: 'kb_article',
        action: 'read',
        domain: 'knowledge',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.kb_article.read',
    },
    'kb_article:create': {
        resource: 'kb_article',
        action: 'create',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.create',
    },
    'kb_article:update': {
        resource: 'kb_article',
        action: 'update',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.update',
    },
    'kb_article:delete': {
        resource: 'kb_article',
        action: 'delete',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.delete',
    },
    'kb_article:approve': {
        resource: 'kb_article',
        action: 'approve',
        domain: 'knowledge',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.kb_article.approve',
    },
    'kb_article:publish': {
        resource: 'kb_article',
        action: 'publish',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.publish',
    },
    'kb_article:export': {
        resource: 'kb_article',
        action: 'export',
        domain: 'knowledge',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.kb_article.export',
    },
    'kb_article:view_history': {
        resource: 'kb_article',
        action: 'view_history',
        domain: 'knowledge',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.kb_article.view_history',
    },
    'kb_article:restore_version': {
        resource: 'kb_article',
        action: 'restore_version',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.restore_version',
    },
    'kb_article:manage_tags': {
        resource: 'kb_article',
        action: 'manage_tags',
        domain: 'knowledge',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.kb_article.manage_tags',
    },
    'kb_article:comment': {
        resource: 'kb_article',
        action: 'comment',
        domain: 'knowledge',
        requiredLevel: 'COMMENTER',
        dangerous: false,
        descriptionKey: 'permission.kb_article.comment',
    },
    'file:read': {
        resource: 'file',
        action: 'read',
        domain: 'file',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.file.read',
    },
    'file:download': {
        resource: 'file',
        action: 'download',
        domain: 'file',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.file.download',
    },
    'file:upload': {
        resource: 'file',
        action: 'upload',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.upload',
    },
    'file:update': {
        resource: 'file',
        action: 'update',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.update',
    },
    'file:delete': {
        resource: 'file',
        action: 'delete',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.delete',
    },
    'file:restore': {
        resource: 'file',
        action: 'restore',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.restore',
    },
    'file:manage_versions': {
        resource: 'file',
        action: 'manage_versions',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.manage_versions',
    },
    'file:manage_folders': {
        resource: 'file',
        action: 'manage_folders',
        domain: 'file',
        requiredLevel: 'EDITOR',
        dangerous: false,
        descriptionKey: 'permission.file.manage_folders',
    },
    'file:manage_acl': {
        resource: 'file',
        action: 'manage_acl',
        domain: 'file',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.file.manage_acl',
    },
    'file:view_quarantined': {
        resource: 'file',
        action: 'view_quarantined',
        domain: 'file',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.file.view_quarantined',
    },
    'file:download_quarantined': {
        resource: 'file',
        action: 'download_quarantined',
        domain: 'file',
        requiredLevel: 'MANAGER',
        dangerous: true,
        descriptionKey: 'permission.file.download_quarantined',
    },
    'file:manage_retention': {
        resource: 'file',
        action: 'manage_retention',
        domain: 'file',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.file.manage_retention',
    },
    'file:view_quota': {
        resource: 'file',
        action: 'view_quota',
        domain: 'file',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.file.view_quota',
    },
    'report:read': {
        resource: 'report',
        action: 'read',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.read',
    },
    'report:read_team': {
        resource: 'report',
        action: 'read_team',
        domain: 'analytics',
        requiredLevel: 'VIEWER',
        dangerous: false,
        descriptionKey: 'permission.report.read_team',
    },
    'report:read_org': {
        resource: 'report',
        action: 'read_org',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.report.read_org',
    },
    'report:create': {
        resource: 'report',
        action: 'create',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.create',
    },
    'report:update': {
        resource: 'report',
        action: 'update',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.update',
    },
    'report:delete': {
        resource: 'report',
        action: 'delete',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.delete',
    },
    'report:export': {
        resource: 'report',
        action: 'export',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.export',
    },
    'report:schedule': {
        resource: 'report',
        action: 'schedule',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.report.schedule',
    },
    'dashboard:read': {
        resource: 'dashboard',
        action: 'read',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.dashboard.read',
    },
    'dashboard:create': {
        resource: 'dashboard',
        action: 'create',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.dashboard.create',
    },
    'dashboard:update': {
        resource: 'dashboard',
        action: 'update',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.dashboard.update',
    },
    'dashboard:delete': {
        resource: 'dashboard',
        action: 'delete',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.dashboard.delete',
    },
    'dashboard:share': {
        resource: 'dashboard',
        action: 'share',
        domain: 'analytics',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.dashboard.share',
    },
    'ai:use': {
        resource: 'ai',
        action: 'use',
        domain: 'ai',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.use',
    },
    'ai:use_document_context': {
        resource: 'ai',
        action: 'use_document_context',
        domain: 'ai',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.use_document_context',
    },
    /**
     * Открыть хранимую трассу ответа — `GET /ai/assistant/answers/{answerId}/trace` (STORY-007-06).
     *
     * **Отдельный ключ, а не доля `ai:use_document_context`.** Задать вопрос и проверить основание
     * ответа — разные действия разных людей: доказательство смотрит тот, кто проверяет результат, а
     * не обязательно тот, кто спрашивал. Слив их в один ключ означал бы, что сузить право спрашивать
     * — а это открытое продуктовое решение первой итерации — нельзя, не отняв заодно возможность
     * проверить уже данные ответы.
     *
     * **Не выдан `auditor`, и это осознанно.** Трасса несёт текст утверждения и цитату пункта, то
     * есть содержимое документов; аудитор читает журналы, а не документы, в них упомянутые
     * (`system-roles.enums.ts`, третье свойство таблицы ролей). Ключ держат те же роли, которым
     * открыт документный контекст модели, — иначе право читать документы через ответ обходилось бы
     * чтением доказательства этого ответа.
     */
    'ai:read_answer_trace': {
        resource: 'ai',
        action: 'read_answer_trace',
        domain: 'ai',
        // `null`, как у всех ключей `ai:*`. Ключи с уровнем в каталоге есть — 102 из 260 (`file:*`,
        // `template:read`), — и именно поэтому они сегодня отказывают всем: ресурсного ACL в продукте
        // нет. Когда он появится, выдача трассы обязана сузиться до документов, доступных читателю, —
        // сейчас она ограничена только арендатором.
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.read_answer_trace',
    },
    'ai:read_own_threads': {
        resource: 'ai',
        action: 'read_own_threads',
        domain: 'ai',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.read_own_threads',
    },
    'ai:read_any_thread': {
        resource: 'ai',
        action: 'read_any_thread',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.read_any_thread',
    },
    'ai:delete_thread': {
        resource: 'ai',
        action: 'delete_thread',
        domain: 'ai',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.delete_thread',
    },
    'ai:run_tools': {
        resource: 'ai',
        action: 'run_tools',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.run_tools',
    },
    'ai:view_usage': {
        resource: 'ai',
        action: 'view_usage',
        domain: 'ai',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai.view_usage',
    },
    'ai:manage_budget': {
        resource: 'ai',
        action: 'manage_budget',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.manage_budget',
    },
    'ai:manage_tool_policy': {
        resource: 'ai',
        action: 'manage_tool_policy',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.manage_tool_policy',
    },
    'ai:configure_providers': {
        resource: 'ai',
        action: 'configure_providers',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.configure_providers',
    },
    'ai:reindex_embeddings': {
        resource: 'ai',
        action: 'reindex_embeddings',
        domain: 'ai',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai.reindex_embeddings',
    },
    'audit:read': {
        resource: 'audit',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.audit.read',
    },
    'audit:read_security': {
        resource: 'audit',
        action: 'read_security',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.audit.read_security',
    },
    'audit:read_access': {
        resource: 'audit',
        action: 'read_access',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.audit.read_access',
    },
    'audit:export': {
        resource: 'audit',
        action: 'export',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.audit.export',
    },
    'audit:manage_retention': {
        resource: 'audit',
        action: 'manage_retention',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.audit.manage_retention',
    },
    'settings:read': {
        resource: 'settings',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.settings.read',
    },
    'settings:update': {
        resource: 'settings',
        action: 'update',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.update',
    },
    'settings:manage_email': {
        resource: 'settings',
        action: 'manage_email',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.manage_email',
    },
    'settings:manage_storage_backend': {
        resource: 'settings',
        action: 'manage_storage_backend',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.manage_storage_backend',
    },
    'settings:manage_backup': {
        resource: 'settings',
        action: 'manage_backup',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.manage_backup',
    },
    'settings:run_backup': {
        resource: 'settings',
        action: 'run_backup',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.settings.run_backup',
    },
    'settings:restore_backup': {
        resource: 'settings',
        action: 'restore_backup',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.restore_backup',
    },
    'settings:view_system_health': {
        resource: 'settings',
        action: 'view_system_health',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.settings.view_system_health',
    },
    'settings:manage_feature_flags': {
        resource: 'settings',
        action: 'manage_feature_flags',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.settings.manage_feature_flags',
    },
    'retention:read': {
        resource: 'retention',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.retention.read',
    },
    'retention:manage': {
        resource: 'retention',
        action: 'manage',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.retention.manage',
    },
    'retention:unlock': {
        resource: 'retention',
        action: 'unlock',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.retention.unlock',
    },
    'api_token:read_own': {
        resource: 'api_token',
        action: 'read_own',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.api_token.read_own',
    },
    'api_token:create_own': {
        resource: 'api_token',
        action: 'create_own',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.api_token.create_own',
    },
    'api_token:revoke_own': {
        resource: 'api_token',
        action: 'revoke_own',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.api_token.revoke_own',
    },
    'api_token:read_any': {
        resource: 'api_token',
        action: 'read_any',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.api_token.read_any',
    },
    'api_token:revoke_any': {
        resource: 'api_token',
        action: 'revoke_any',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.api_token.revoke_any',
    },
    'notification:read_own': {
        resource: 'notification',
        action: 'read_own',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.notification.read_own',
    },
    'notification:manage_preferences_own': {
        resource: 'notification',
        action: 'manage_preferences_own',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.notification.manage_preferences_own',
    },
    'notification:manage_templates': {
        resource: 'notification',
        action: 'manage_templates',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.notification.manage_templates',
    },
    'job:read': {
        resource: 'job',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.job.read',
    },
    'job:retry': {
        resource: 'job',
        action: 'retry',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.job.retry',
    },
    'search:reindex': {
        resource: 'search',
        action: 'reindex',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.search.reindex',
    },
    'ai_provider:read': {
        resource: 'ai_provider',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.ai_provider.read',
    },
    /**
     * Настройка провайдера — админская операция: она решает, куда уходят документы организации.
     * Поэтому `dangerous`, несмотря на безобидное слово «настройка».
     */
    'ai_provider:write': {
        resource: 'ai_provider',
        action: 'write',
        domain: 'platform',
        requiredLevel: null,
        dangerous: true,
        descriptionKey: 'permission.ai_provider.write',
    },
    /** Журнал выходов за периметр читает аудитор; остальным ресурса не существует (404). */
    'external_call_log:read': {
        resource: 'external_call_log',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.external_call_log.read',
    },
    'evaluation_run:read': {
        resource: 'evaluation_run',
        action: 'read',
        domain: 'platform',
        requiredLevel: null,
        dangerous: false,
        descriptionKey: 'permission.evaluation_run.read',
    },
};
export const PERMISSION_SET = new Set(PERMISSIONS);
export const isPermissionKey = (value) => PERMISSION_SET.has(value);
/** `null` означает, что ключ не несёт ресурсного контекста; `undefined` ответом быть не может. */
export const requiredLevel = (key) => PERMISSION_META[key].requiredLevel;
//# sourceMappingURL=permissions.catalog.js.map